//replaces widget-container.component.ts
//replaces widget-container.html

import type { FC } from "react";
import { memo, useEffect, useMemo, useRef, useState } from "react";

import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";

import { Dialog } from "@similarweb/ui-components";

import { updateUniqueSegmentsIds } from "app/actions/segmentsModuleActions";
import { swSettings } from "app/common/services/swSettings";
import DashboardService from "app/components/dashboard/services/DashboardService";
import {
    getSegmentsIdsFromGroup,
    mergeSegmentIds,
} from "app/components/dashboard/widget-wizard/components/segments/helpers";
import {
    selectdUserSegmentsGroups,
    selectUniqueSegments,
} from "app/components/dashboard/widget-wizard/components/segments/selectors";
import { DASHBOARD_ID_PREVIEW_MODE, INDUSTRY_FAMILY_NAME } from "app/components/dashboard/widget-wizard/constants";
import { getCustomIndustryIdFromModel } from "app/components/dashboard/widget-wizard/utils/dashboardWizardUtils";
import type { IDashboardWidgetInterface } from "app/components/widget/utils/DashboardWidgetsInterfaces-v2";
import type { IWidget } from "app/components/widget/widget-types/Widget";
import { SEGMENTS_FAMILY_NAME } from "app/components/widget/widget-utilities/segments/constants";
import { WidgetExportPptButton } from "app/components/widget/WidgetExportPptButton";
import { WidgetFooter } from "app/components/widget/WidgetFooter";
import { WidgetComponentV2 } from "app/dashboards/view-components/WidgetComponent-v2";
import { WidgetDraggableHolder } from "app/dashboards/view-components/WidgetContainerTemplateStyles-v2";
import { WidgetSubtitleV2 } from "app/dashboards/view-components/WidgetSubtitle-v2";
import { i18nFilter } from "app/filters/i18nFilter";
import { PptExportService } from "app/services/PptExportService/PptExportService";
import type { IPptExportRequest } from "app/services/PptExportService/PptExportServiceTypes";
import { SwTrack } from "app/services/SwTrack";
import { DeprecatedWidgetComponent } from "./DeprecatedWidgetComponent";
import { WidgetLegendContainerV2 } from "./widget-legend/WidgetLegendContainer-v2";
import { StoreAnalysisGraphTitleTemplate } from "./widget-title/StoreAnalysisGraphTitleTemplate";
import { TableCompareTitleTemplate } from "./widget-title/TableCompareTitleTemplate";
import { WidgetCustomTitleTemplate } from "./widget-title/WidgetCustomTitleTemplate";
import { WidgetTitleMobileDesktopTemplate } from "./widget-title/WidgetTitleMobileDesktopTemplate";
import { WidgetTitleTemplate } from "./widget-title/WidgetTitleTemplate";
import { WidgetMenu } from "./WidgetMenu";
import { WidgetResizeV2 } from "./WidgetResize-v2";
import { allTrackers } from "app/services/track/track";

const i18n = i18nFilter();
const widgetDialogs = {
    delete: {
        title: i18n("home.dashboards.widget.menu.confirmation"),
        confirm: i18n("home.dashboards.widget.menu.delete"),
        cancel: i18n("mediaBuy.sideNav.popup.cancel"),
    },
};

const titleComponents = {
    ["widget-custom-title"]: WidgetCustomTitleTemplate,
    ["widget-title-mobile-desktop"]: WidgetTitleMobileDesktopTemplate,
    ["table-compare-title"]: TableCompareTitleTemplate,
    ["store-analysis-graph-title"]: StoreAnalysisGraphTitleTemplate,
    ["widget-title"]: WidgetTitleTemplate,
};

interface IWidgetContainerTemplate extends IDashboardWidgetInterface {
    titleEditable: boolean;
    widgetTitle: any;
    cancelEditTitle: any;
    saveTitle: any;
    editTitle: any;
    onEdit(widget: IWidget<any>): void;
    onToggleEnlarge(): void;
    className?: string;
    isStatic?: boolean;
}

export const WidgetContainerTemplateV2: FC<IWidgetContainerTemplate> = (props) => {
    const {
        widget,
        titleEditable,
        widgetTitle,
        cancelEditTitle,
        saveTitle,
        editTitle,
        onEdit,
        onToggleEnlarge,
        className,
        setChart,
        isStatic,
    } = props;
    const dispatch = useDispatch();
    const defaultUniqueSegments = useSelector(selectUniqueSegments);
    const segmentsGroups = useSelector(selectdUserSegmentsGroups);

    const {
        beingEdited,
        viewOptions,
        dashboardId,
        viewData,
        widgetState,
        hideAddToDashboard,
        originalData,
        type,
        legendClass,
    } = widget;
    const {
        noBoxShadow,
        showTopLine,
        noBottomPadding,
        noTopPadding,
        showFrame,
        frameClass,
        showLegend,
        hook,
        target,
        ctaButtonTracking,
        ctaButton,
        link,
        floatingLegend,
        showSettings,
    } = viewOptions;
    const isDeprecated = widget._metricConfig.isDeprecated;

    const pptService = useRef(null);
    useEffect(() => {
        pptService.current = new PptExportService();
    }, []);

    const isSharedWithMe = DashboardService.dashboard?.isSharedWithMe;
    // Extracts the file name from the template path string, without extension
    const TitleComponent = useMemo(() => {
        const titleTemplate = widget.getTitleTemplate();
        const fileNameStartPosition = titleTemplate.lastIndexOf("/");
        const templateName = titleTemplate.substring(fileNameStartPosition + 1, titleTemplate.length - 5);
        return titleComponents[templateName];
    }, [widget]);

    const hasPptExportAccess = swSettings.user.hasPptExport;
    //TODO: ilan - need to get the 3 implementations of custom to code
    const CustomWidgetContainer = useMemo<FC<IWidgetContainerTemplate> | null>(() => {
        // support custom containers
        if (widget?.container) {
            if (typeof widget.container !== "string") {
                return widget.container;
            }
        }
        return null;
    }, [widget]);

    function handleSetChart(chartRef) {
        setChart?.(chartRef);
    }

    const [dialogOptions, setDialogOptions] = useState({ title: "", confirm: "", cancel: "" });
    const [showDialog, setShowDialog] = useState(false);

    async function dialogOnConfirm() {
        SwTrack.all.trackEvent("Drop Down", "click", "Widget Settings/delete/Yes");
        await DashboardService.deleteWidget(widget);
    }

    function dialogOnCancel() {
        setShowDialog(false);
        SwTrack.all.trackEvent("Drop Down", "click", "Widget Settings/delete/No");
    }

    function handleDelete() {
        setShowDialog(true);
        setDialogOptions(widgetDialogs.delete);
    }

    const handleDeleteOfDeprecated = async () => {
        await DashboardService.deleteWidget(widget);
    };

    async function handleDuplicate() {
        const widgetModel = widget.getWidgetModel();
        const widgetConfig = widget?._widgetConfig;

        let segmentId;
        let segmentsGroupId;
        let customIndustryId;

        if (widgetModel?.family === SEGMENTS_FAMILY_NAME || widgetModel?.family === INDUSTRY_FAMILY_NAME) {
            const selectedGroupId = widget?._widgetConfig?.properties?.filters?.segmentsGroupId;
            let mergedSegmentsIds;

            if (selectedGroupId && segmentsGroups) {
                const segmentsIdsFromGroup = getSegmentsIdsFromGroup(selectedGroupId, segmentsGroups);

                mergedSegmentsIds = mergeSegmentIds(defaultUniqueSegments, segmentsIdsFromGroup.ids);
            } else {
                const singleSegmentIds: string = widget?._params?.segmentId || widget?._params?.keys;

                mergedSegmentsIds = mergeSegmentIds(defaultUniqueSegments, [singleSegmentIds]);
            }

            dispatch(updateUniqueSegmentsIds(mergedSegmentsIds));

            segmentId = widgetConfig?.segmentId || widgetConfig?.filters?.segmentId;

            segmentsGroupId = widgetConfig?.segmentsGroupId || widgetConfig?.filters?.segmentsGroupId;

            customIndustryId = widgetConfig?.customIndustryId || getCustomIndustryIdFromModel(widgetModel?.key);
        }

        await DashboardService.addWidget({
            properties: widget.getProperties(),
            pos: { sizeX: widget.pos.sizeX, sizeY: widget.pos.sizeY },
            dashboardId: widget.dashboardId,
            segmentId,
            segmentsGroupId,
            ...(customIndustryId && { customIndustryId }),
        });
    }

    useEffect(() => {
        if (!widget) {
            return;
        }
        widget.on("widget.showDialog", (event, type) => {
            console.log("NewWidgetContainerTemplare widget.showDialog");
            setDialogOptions(widgetDialogs[type]);
            setShowDialog(true);
        });
    }, [widget]);

    const onExportClick = () => {
        allTrackers.dispatchTrackEvent("Button", "click", "Dashboard Widget Export PPT");
        const request: IPptExportRequest = {
            name: widgetTitle,
            metrics: [widget.getDataForPpt()],
            meta: {
                userEmail: swSettings?.user?.username,
            },
        };
        pptService.current.exportPpt(request);
    };

    const titleClickable =
        !widget._metricConfig.disableTitleLink &&
        !isDeprecated &&
        !(
            widget.readOnly &&
            widget.viewData?.proUrl?.indexOf(`*${encodeURI(widget.getWidgetModel().key[0].name)}`) > -1
        );
    const titleComponentProps = {
        widget,
        titleEditable,
        titleClickable,
        widgetTitle,
        cancelEditTitle,
        saveTitle,
        editTitle,
    };

    const topClasses = classNames(`${viewData.family} widget-state-${widgetState}`, {
        "swWidget--top": showTopLine && !dashboardId,
        "swWidget--boxShadow": !noBoxShadow,
    });

    const frameClasses = classNames("swWidget-frame grid-no-drag", {
        [frameClass]: frameClass,
        "swWidget-frame--noFrame": !showFrame,
        "swWidget-frame--noBottomPadding": noBottomPadding,
    });

    if (CustomWidgetContainer) {
        return <CustomWidgetContainer {...props} />;
    }

    if (beingEdited) {
        return null;
    }

    if (!widget) {
        return <h1>No widget Instance</h1>;
    }

    return (
        <div
            className={classNames(
                "swWidget fadeIn swWidget--boxShadowZ1",
                {
                    "swWidget--top": !widget.viewOptions.showTopLine && !widget.dashboardId,
                },
                widget.viewData.family,
                `widget-state-${widget.widgetState}`,
                className,
            )}
        >
            <div className={topClasses}>
                {!isStatic ? (
                    <WidgetDraggableHolder className="widgetDraggableHolder">
                        <svg
                            width="18"
                            height="8"
                            viewBox="0 0 18 8"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <circle
                                cx="1.5"
                                cy="1.5"
                                r="1.5"
                                fill="#E6E9EC"
                            />
                            <circle
                                cx="16.5"
                                cy="1.5"
                                r="1.5"
                                fill="#E6E9EC"
                            />
                            <circle
                                cx="1.5"
                                cy="6.5"
                                r="1.5"
                                fill="#E6E9EC"
                            />
                            <circle
                                cx="16.5"
                                cy="6.5"
                                r="1.5"
                                fill="#E6E9EC"
                            />
                            <circle
                                cx="6.5"
                                cy="1.5"
                                r="1.5"
                                fill="#E6E9EC"
                            />
                            <circle
                                cx="6.5"
                                cy="6.5"
                                r="1.5"
                                fill="#E6E9EC"
                            />
                            <circle
                                cx="11.5"
                                cy="1.5"
                                r="1.5"
                                fill="#E6E9EC"
                            />
                            <circle
                                cx="11.5"
                                cy="6.5"
                                r="1.5"
                                fill="#E6E9EC"
                            />
                        </svg>
                    </WidgetDraggableHolder>
                ) : null}

                {/*Widget title*/}
                <div className={frameClasses}>
                    <TitleComponent {...titleComponentProps} />

                    <div className="u-clear-both" />

                    {showSettings && !isSharedWithMe ? (
                        <div className="swWidgetCog grid-no-drag">
                            <WidgetMenu
                                widget={widget}
                                onEdit={onEdit}
                                onDelete={isDeprecated ? handleDeleteOfDeprecated : handleDelete}
                                onDuplicate={handleDuplicate}
                            ></WidgetMenu>
                        </div>
                    ) : null}
                    {dashboardId && hasPptExportAccess && !isDeprecated ? (
                        <div className="swWidgetCog grid-no-drag">
                            <WidgetExportPptButton
                                onClick={onExportClick}
                                isDisabled={!widget.isPptSupported()}
                            />
                        </div>
                    ) : null}

                    <WidgetSubtitleV2 widget={widget} />
                    {widget.hasShowMoreButton() &&
                    widget?.dashboardId !== DASHBOARD_ID_PREVIEW_MODE &&
                    !isDeprecated ? (
                        <WidgetResizeV2
                            widget={widget}
                            onToggleEnlarge={onToggleEnlarge}
                        />
                    ) : null}

                    {/*Widget legend*/}
                    {showLegend && !!originalData && !isDeprecated ? (
                        <div
                            className={classNames(
                                "swWidget-legend",
                                {
                                    "swWidget-legend--left": floatingLegend,
                                    "table-legend": type.toLowerCase().indexOf("table") > -1,
                                },
                                widget.legendClass,
                            )}
                        >
                            <WidgetLegendContainerV2
                                widget={widget}
                                chartConfig={widget.chartConfig}
                                legendData={widget.widgetState < 2}
                                items={widget.legendItems}
                                trackName={widget.viewData.trackName}
                                trackWidgetType={widget.type}
                                onSeriesToggle={widget.onSeriesToggle}
                                legendsColorSource={widget.viewData.legendsColorSource}
                                legendContainerClass={widget.viewData.legendContainerClass}
                                customTemplate={widget.customLegendTemplate}
                                useNewLegends={widget.viewOptions.useNewLegends}
                                useBulletLegends={widget.viewOptions.useBulletLegends}
                                gridColumnGap={widget.viewOptions.legendsGridColumnGap}
                                reactLegendComponent={widget.reactLegendComponent}
                            />
                        </div>
                    ) : null}

                    {/*Widget component*/}
                    {isDeprecated ? (
                        <DeprecatedWidgetComponent
                            title={widget._metricConfig.deprecated?.title}
                            subtitle={widget._metricConfig.deprecated?.subtitle}
                        />
                    ) : (
                        <WidgetComponentV2
                            widget={widget}
                            setChart={handleSetChart}
                        />
                    )}
                </div>

                {/*Widget footer*/}
                {!!widget.viewOptions.ctaButton && !isDeprecated ? (
                    <WidgetFooter
                        href={widget.viewOptions.link}
                        trackingLabel={widget.viewOptions.ctaButtonTracking}
                        text={widget.viewOptions.ctaButton}
                        target={widget.viewOptions.target}
                    />
                ) : null}
            </div>
            {showDialog ? (
                <div className="widget-dialog-container grid-no-drag">
                    <Dialog
                        titleText={dialogOptions.title}
                        confirmText={dialogOptions.confirm}
                        cancelText={dialogOptions.cancel}
                        onConfirm={dialogOnConfirm}
                        onCancel={dialogOnCancel}
                    />
                </div>
            ) : null}
        </div>
    );
};

const MemoizedWidgetContainerTemplateV2 = memo(WidgetContainerTemplateV2);
